import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdSlot from '../components/AdSlot';
import HotNewsFeed from '../components/HotNewsFeed';
import { MobileHotNewsToggle } from '../components/MobileHotNewsToggle';
import { StoryFeedList } from '../components/StoryFeedList';
import { useStoriesFeed } from '../hooks/useStoriesFeed';
import { useInfiniteScrollSentinel } from '../hooks/useInfiniteScrollSentinel';
import { Loader2, Info } from 'lucide-react';

const SECTION_DESCRIPTIONS = {
  all: 'See how different sources report the same story - articles ordered by stance with the most factual in the middle',
  bubble: 'Discover stories from sources outside your filter bubble - break free from echo chambers',
  blindsyde: 'Important stories covered by only 1-2 sources that mainstream media might be missing',
  timeline: 'Follow breaking stories as they develop - events ordered chronologically with factual reporting'
};

const HomePage = () => {
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view') || 'all';
  const [showMobileHotNews, setShowMobileHotNews] = useState(false);

  const { mainStories, dayGroups, hotStories, loading, error, hasMore, loadingMore, loadMore, refetch } = useStoriesFeed(view);
  const sentinelRef = useInfiniteScrollSentinel(loadMore, !!dayGroups && hasMore && !loading);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="loading-spinner">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading news stories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center" data-testid="error-message">
          <p className="text-red-800 font-medium">Error loading stories</p>
          <p className="text-red-600 text-sm mt-2">{error}</p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            data-testid="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8" data-testid="home-page">
      {/* Section Description */}
      <div className="flex items-start gap-2 sm:gap-3 mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 border border-blue-100 rounded-lg">
        <Info className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
          {SECTION_DESCRIPTIONS[view]}
        </p>
      </div>

      <MobileHotNewsToggle
        hotStories={hotStories}
        isOpen={showMobileHotNews}
        onToggle={() => setShowMobileHotNews(!showMobileHotNews)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6 lg:gap-8">
        {/* Main Feed */}
        <div>
          <AdSlot slotId="top-banner" format="horizontal" className="mb-6 sm:mb-8" />

          <StoryFeedList stories={mainStories} view={view} dayGroups={dayGroups} />

          {dayGroups && (
            <div ref={sentinelRef} className="py-8 text-center" data-testid="infinite-scroll-sentinel">
              {loadingMore && <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" />}
              {!hasMore && mainStories.length > 0 && (
                <p className="text-sm text-gray-400">You've caught up on recent stories.</p>
              )}
            </div>
          )}

          {mainStories.length > 0 && (
            <AdSlot slotId="bottom-banner" format="horizontal" className="mt-6 sm:mt-8" />
          )}
        </div>

        {/* Hot News - Desktop Only */}
        <div className="hidden lg:block space-y-6">
          <HotNewsFeed stories={hotStories} />
          <AdSlot slotId="sidebar-bottom" format="vertical" />
        </div>
      </div>
    </div>
  );
};

export default HomePage;
